import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, TrendingUp, TrendingDown, BarChart3, FolderTree, Menu, X, LogOut } from 'lucide-react';
import { useState } from 'react';

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/income', label: 'Income', icon: TrendingUp },
    { path: '/expenses', label: 'Expenses', icon: TrendingDown },
    { path: '/reports', label: 'Reports', icon: BarChart3 },
    { path: '/categories', label: 'Categories', icon: FolderTree },
  ];

  const handleLogout = () => {
    localStorage.removeItem('ego_auth_token');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card/50 backdrop-blur-md border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-black font-heading tracking-tight text-primary">
                EGO Cash Flow
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1">Income & Expense Tracking</p>
            </div>
            {/* Mobile menu button */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleLogout}
                className="hidden md:flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-primary hover:bg-accent rounded-lg transition-colors"
                data-testid="logout-button"
                title="Logout"
              >
                <LogOut size={18} strokeWidth={1.5} />
                Logout
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-foreground hover:bg-accent rounded-lg"
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Desktop Navigation */}
      <nav className="hidden md:block bg-secondary border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors duration-200 border-b-2 ${
                    isActive
                      ? 'border-primary text-primary bg-card'
                      : 'border-transparent text-muted-foreground hover:text-primary hover:bg-card/50'
                  }`}
                >
                  <Icon size={18} strokeWidth={1.5} />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-secondary border-b border-border">
          <div className="px-4 py-2 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-3 px-4 py-3 font-medium text-sm rounded-lg transition-colors ${
                    isActive
                      ? 'bg-card text-primary'
                      : 'text-muted-foreground hover:text-primary hover:bg-card/50'
                  }`}
                >
                  <Icon size={20} strokeWidth={1.5} />
                  {item.label}
                </Link>
              );
            })}
            <button
              onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
              data-testid="mobile-logout-button"
              className="w-full flex items-center gap-3 px-4 py-3 font-medium text-sm rounded-lg text-muted-foreground hover:text-primary hover:bg-card/50 transition-colors"
            >
              <LogOut size={20} strokeWidth={1.5} />
              Logout
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {children}
      </main>
    </div>
  );
};
